import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { TranslatePipe } from '../languages/translate.pipe';
import { ChartModule } from 'primeng/chart';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { PanelModule } from 'primeng/panel';
import { COMMON_IMPORTS } from '../common-imports';
import { PremyopeRiskScoreCategory } from '../backend-response-models/calc-enums.helper';
import Mustache from 'mustache';

@Component({
  selector: 'app-parent-paragraph',
  standalone: true,
  imports: [...COMMON_IMPORTS, PanelModule, DropdownModule, ChartModule, ChipModule],
  templateUrl: './parent-paragraph.component.html',
  styleUrl: './parent-paragraph.component.css',
})
export class ParentParagraphComponent implements OnInit, OnChanges {
  @Input()
  personalization?: string;

  @Input({ required: true })
  retestMonths!: number;

  @Input({ required: true })
  parentParagraphType!: ParentParagraphType;

  @Input()
  premyopeRiskScoreCatagory?: PremyopeRiskScoreCategory;

  public paragraphTitle: string = 'NONE';
  public paragraph1Content: string = 'NONE';
  public showAdviceBulletpoints: boolean = false;
  public showTreatmentInBulletPoints: boolean = false;

  private titleKeys: Record<ParentParagraphType, string> = {
    [ParentParagraphType.WHAT_IS_MYOPIA]: 'TITLE_WHAT_IS_MYOPIA',
    [ParentParagraphType.WHAT_IS_MYOPIA_MATTER]: 'TITLE_WHAT_IS_MYOPIA_MATTER',
    [ParentParagraphType.HOW_DO_MY_CHILDS_RESULTS_COMPARE_PREMYOPE]: 'TITLE_HOW_DO_MY_CHILDS_RESULTS_COMPARE',
    [ParentParagraphType.HOW_DO_MY_CHILDS_RESULTS_COMPARE_MYOPIC]: 'TITLE_HOW_DO_MY_CHILDS_RESULTS_COMPARE',
    [ParentParagraphType.WHY_DOES_EYE_SIZE_MATTER]: 'TITLE_AMOUNT_MYOPIA_MATTER',
    [ParentParagraphType.SLOWDOWN_MYOPIA_PREMYOPE]: 'TITLE_MY_CHILD_NOT_MYOPIC_KEEP_IT_THAT_WAY',
    [ParentParagraphType.SLOWDOWN_MYOPIA_MYOPIC]: 'TITLE_SLOW_DOWN_MYOPIA',
    [ParentParagraphType.SLOWDOWN_MYOPIA_MYOPIC_WITH_TREATMENT]: 'TITLE_SLOW_DOWN_MYOPIA',
    [ParentParagraphType.WHAT_NEXT_PREMYOPE]: 'TITLE_WHAT_NEXT',
    [ParentParagraphType.WHAT_NEXT_MYOPIC]: 'TITLE_WHAT_NEXT',
    [ParentParagraphType.FURTHER_RESOURCES]: 'TITLE_FURTHER_RESOURCES',
  };

  private paragraph1Keys: Record<ParentParagraphType, string> = {
    [ParentParagraphType.WHAT_IS_MYOPIA]: 'TEMPLATE_WHAT_IS_MYOPIA',
    [ParentParagraphType.WHAT_IS_MYOPIA_MATTER]: 'TEMPLATE_WHAT_IS_MYOPIA_MATTER',
    [ParentParagraphType.HOW_DO_MY_CHILDS_RESULTS_COMPARE_PREMYOPE]: 'TEMPLATE_HOW_DO_MY_CHILDS_RESULTS_COMPARE_PREMYOPE',
    [ParentParagraphType.HOW_DO_MY_CHILDS_RESULTS_COMPARE_MYOPIC]: 'TEMPLATE_HOW_DO_MY_CHILDS_RESULTS_COMPARE_MYOPIC',
    [ParentParagraphType.WHY_DOES_EYE_SIZE_MATTER]: 'TEMPLATE_WHY_DOES_EYE_SIZE_MATTER',
    [ParentParagraphType.SLOWDOWN_MYOPIA_PREMYOPE]: 'TEMPLATE_SLOWDOWN_MYOPIA_PREMYOPE',
    [ParentParagraphType.SLOWDOWN_MYOPIA_MYOPIC]: 'TEMPLATE_SLOWDOWN_MYOPIA_MYOPIC',
    [ParentParagraphType.SLOWDOWN_MYOPIA_MYOPIC_WITH_TREATMENT]: 'TEMPLATE_SLOWDOWN_MYOPIA_MYOPIC_WITH_TREATMENT',
    [ParentParagraphType.WHAT_NEXT_PREMYOPE]: 'TEMPLATE_WHAT_NEXT_PREMYOPE',
    [ParentParagraphType.WHAT_NEXT_MYOPIC]: 'TEMPLATE_WHAT_NEXT_MYOPIC',
    [ParentParagraphType.FURTHER_RESOURCES]: 'TEMPLATE_FURTHER_RESOURCES',
  };

  private premyopeRiskScoreCategoryKeys: Record<PremyopeRiskScoreCategory, string> = {
    [PremyopeRiskScoreCategory.LITTLE_TO_NO_RISK]: 'PREMYOPE_RISK_LITTLE_NONE',
    [PremyopeRiskScoreCategory.LITTLE_RISK]: 'PREMYOPE_RISK_LOW',
    [PremyopeRiskScoreCategory.MODERATE_RISK]: 'PREMYOPE_RISK_MODERATE',
    [PremyopeRiskScoreCategory.HIGH_RISK]: 'PREMYOPE_RISK_HIGH',
  };

  constructor(private translationPipe: TranslatePipe) {}

  ngOnInit() {
    this.refreshContent();
  }

  // eslint-disable-next-line no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this.refreshContent();
  }

  refreshContent() {
    this.refreshTitle();
    this.refreshPictureBulletPoints();
    const templateInputs = this.getAllPlaceholders();
    this.refreshParagraph1Content(templateInputs);
  }

  refreshTitle() {
    const titleDictKey = this.titleKeys[this.parentParagraphType];
    this.paragraphTitle = this.translationPipe.transform(titleDictKey);
  }

  refreshParagraph1Content(templateInputs: TemplateInputs) {
    const paragraph1DictKey = this.paragraph1Keys[this.parentParagraphType];
    const moustacheTemplate = this.translationPipe.transform(paragraph1DictKey);
    const renderedTemplate = Mustache.render(moustacheTemplate, templateInputs);
    this.paragraph1Content = renderedTemplate;
  }

  refreshPictureBulletPoints() {
    const releventParagraphTypes = [
      ParentParagraphType.SLOWDOWN_MYOPIA_PREMYOPE,
      ParentParagraphType.SLOWDOWN_MYOPIA_MYOPIC,
      ParentParagraphType.SLOWDOWN_MYOPIA_MYOPIC_WITH_TREATMENT,
    ];

    const isSlowdownParagraph = releventParagraphTypes.includes(this.parentParagraphType);
    const isSlowdownWithTreatment =
      this.parentParagraphType === ParentParagraphType.SLOWDOWN_MYOPIA_MYOPIC_WITH_TREATMENT ||
      this.parentParagraphType === ParentParagraphType.SLOWDOWN_MYOPIA_MYOPIC;

    this.showAdviceBulletpoints = isSlowdownParagraph;
    this.showTreatmentInBulletPoints = isSlowdownWithTreatment;
  }

  getAllPlaceholders(): TemplateInputs {
    const { LENGTH, LENGTH_SUFFIX } = this.getLengthAndLengthSuffix();
    const PREMYOPE_RISK = this.getSingleMyopeRiskScoreCategory();
    const PERSONALIZED_NAME = this.getPersonalization();
    const PERSONALIZED_NAME_CAPTIALIZED = PERSONALIZED_NAME.charAt(0).toUpperCase() + PERSONALIZED_NAME.slice(1);

    return {
      PERSONALIZED_NAME,
      LENGTH,
      LENGTH_SUFFIX,
      PREMYOPE_RISK,
      PERSONALIZED_NAME_CAPTIALIZED,
    };
  }

  getPersonalization() {
    if (!this.personalization) {
      const noName = this.translationPipe.transform('NO_NAME_NAME');
      return noName.toLocaleLowerCase();
    }

    return this.personalization;
  }

  getSingleMyopeRiskScoreCategory() {
    if (!this.premyopeRiskScoreCatagory) {
      return 'NO_PREMYOPE_RISK_SCORE_CATEGORY';
    }

    const myopeRiskKey = this.premyopeRiskScoreCategoryKeys[this.premyopeRiskScoreCatagory];

    return this.translationPipe.transform(myopeRiskKey);
  }

  getLengthAndLengthSuffix() {
    let LENGTH = this.retestMonths;
    let LENGTH_SUFFIX = this.translationPipe.transform('MONTHS');

    if (this.retestMonths == 12) {
      LENGTH = this.retestMonths / 12;
      LENGTH_SUFFIX = this.translationPipe.transform('YEAR');
    } else if (this.retestMonths == 24) {
      LENGTH = this.retestMonths / 12;
      LENGTH_SUFFIX = this.translationPipe.transform('YEARS');
    } else {
      LENGTH = this.retestMonths;
      LENGTH_SUFFIX = this.translationPipe.transform('MONTHS');
    }

    return { LENGTH, LENGTH_SUFFIX };
  }
}

export interface TemplateInputs {
  PERSONALIZED_NAME: string;
  LENGTH: number;
  LENGTH_SUFFIX: string;
  PREMYOPE_RISK: string;
  PERSONALIZED_NAME_CAPTIALIZED: string;
}

export enum ParentParagraphType {
  WHAT_IS_MYOPIA = 'TEMPLATE_WHAT_IS_MYOPIA',
  WHAT_IS_MYOPIA_MATTER = 'TEMPLATE_WHAT_IS_MYOPIA_MATTER',
  HOW_DO_MY_CHILDS_RESULTS_COMPARE_PREMYOPE = 'TEMPLATE_HOW_DO_MY_CHILDS_RESULTS_COMPARE_PREMYOPE',
  HOW_DO_MY_CHILDS_RESULTS_COMPARE_MYOPIC = 'TEMPLATE_HOW_DO_MY_CHILDS_RESULTS_COMPARE_MYOPIC',
  WHY_DOES_EYE_SIZE_MATTER = 'TEMPLATE_WHY_DOES_EYE_SIZE_MATTER',
  SLOWDOWN_MYOPIA_PREMYOPE = 'TEMPLATE_SLOWDOWN_MYOPIA_PREMYOPE',
  SLOWDOWN_MYOPIA_MYOPIC = 'TEMPLATE_SLOWDOWN_MYOPIA_MYOPIC',
  SLOWDOWN_MYOPIA_MYOPIC_WITH_TREATMENT = 'TEMPLATE_SLOWDOWN_MYOPIA_MYOPIC_WITH_TREATMENT',
  WHAT_NEXT_PREMYOPE = 'TEMPLATE_WHAT_NEXT_PREMYOPE',
  WHAT_NEXT_MYOPIC = 'TEMPLATE_WHAT_NEXT_MYOPIC',
  FURTHER_RESOURCES = 'TEMPLATE_FURTHER_RESOURCES',
}
